import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import styled from '@emotion/styled'

import uniqid from 'uniqid'
import colornames from '../../design/color-names'

import {
  Link,
  LINK_VARIANT_DEFAULT_NEGATIVE,
  LINK_VARIANT_EXTRALIGHT
} from '../link'
import { IconUser } from '../SVGIcons'

export const TRUSTED_SMS_INPUT_VARIANT_NEUTRAL = 'neutral'
export const TRUSTED_SMS_INPUT_VARIANT_SUCCESS = 'success'
export const TRUSTED_SMS_INPUT_VARIANT_UNVERIFIED = 'unverified'
export const TRUSTED_SMS_INPUT_VARIANT_VERIFICATION = 'verification'
export const TRUSTED_SMS_INPUT_VARIANT_BLOCKED = 'blocked'
export const TRUSTED_SMS_INPUT_VARIANT_INVALID = 'invalid'

const renderInputVariant = variant => {
  switch (variant) {
    case TRUSTED_SMS_INPUT_VARIANT_NEUTRAL:
      return `border-color: ${colornames.geyser} !important; `
    case TRUSTED_SMS_INPUT_VARIANT_SUCCESS:
      return `border-color: #6DC33A !important;`
    case TRUSTED_SMS_INPUT_VARIANT_UNVERIFIED:
    case TRUSTED_SMS_INPUT_VARIANT_VERIFICATION:
    case TRUSTED_SMS_INPUT_VARIANT_BLOCKED:
    case TRUSTED_SMS_INPUT_VARIANT_INVALID:
      return `border-color: ${colornames.sunsetOrange} !important;`
  }
}

const renderHintContainerVariant = variant => {
  switch (variant) {
    case TRUSTED_SMS_INPUT_VARIANT_INVALID:
      return `color: ${colornames.sunsetOrange};
      padding-top: 8px;`
    case TRUSTED_SMS_INPUT_VARIANT_UNVERIFIED:
    case TRUSTED_SMS_INPUT_VARIANT_VERIFICATION:
    case TRUSTED_SMS_INPUT_VARIANT_BLOCKED:
      return `color: ${colornames.boulder};
        background-color: #fb5959;
        color: ${colornames.pureWhite};
        padding: 7px 10px 8px;
        border-bottom-right-radius: 4px;
        border-bottom-left-radius: 4px;
      `
  }
}

const StyledContainer = styled.div`
  position: relative;
`

const StyledInput = styled.input`
  box-sizing: border-box;
  display: inline-block;
  width: 100%;
  height: 36px;

  border-style: solid;
  border-width: 1px;
  border-radius: 4px;

  padding: 0 8px;
  font-size: 15px;
  line-height: 36px;
  color: ${colornames.tundora};
  background-color: ${colornames.pureWhite};

  transition: all 0.3s ease;

  ${({ variant }) => renderInputVariant(variant)}

  &:focus {
    outline: none;
    border-color: ${colornames.geyser};
  }
`

const StyledHintContainer = styled.div`
  display: flex;
  align-items: center;

  position: relative;
  top: -4px;

  font-weight: 500;
  font-size: 13px;
  padding-top: 4px;
  letter-spacing: 0.03em;

  a:hover {
    color: ${colornames.mineShaft};
  }

  ${({ variant }) => renderHintContainerVariant(variant)}
`

const StyledHint = styled.span`
  padding-right: 0.3rem;
`

const StyledAliasIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 30px;
  width: 40px;
  margin: 3px;
  background: ${colornames.pureWhite};
  text-align: center;
  line-height: 36px;
  border-left: 1px solid ${colornames.geyser};
  cursor: pointer;
  font-size: 16px;
  > a {
    display: block;
    color: var(--silver-chalice);
  }
`

const variantsWithNotes = [
  TRUSTED_SMS_INPUT_VARIANT_UNVERIFIED,
  TRUSTED_SMS_INPUT_VARIANT_VERIFICATION,
  TRUSTED_SMS_INPUT_VARIANT_BLOCKED,
  TRUSTED_SMS_INPUT_VARIANT_INVALID
]

export const TrustedSmsInput = ({
  id,
  className,
  value,
  placeholder,
  aliasIcon,
  onChange,
  onVerifyClick,
  onAliasClick,
  dataAttributeTest,
  variant,
  i18n,
  required
}) => {
  const { current: inputUuid } = useRef(id || uniqid('trusted-sms-input-'))

  return (
    <>
      <StyledContainer
        className={className}
        data-mu-test={classnames('trusted-sms-input', dataAttributeTest)}
      >
        <StyledInput
          id={inputUuid}
          type='text'
          maxLength={500}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          variant={variant}
          required={required}
        />
        {aliasIcon && (
          <StyledAliasIcon>
            <Link
              variant={LINK_VARIANT_EXTRALIGHT}
              onClick={onAliasClick}
              data-mu-test='alias-icon'
            >
              <IconUser />
            </Link>
          </StyledAliasIcon>
        )}
        {variantsWithNotes.includes(variant) && (
          <StyledHintContainer variant={variant}>
            {variant === TRUSTED_SMS_INPUT_VARIANT_INVALID && (
              <StyledHint>{i18n.invalid}</StyledHint>
            )}
            {variant === TRUSTED_SMS_INPUT_VARIANT_UNVERIFIED && (
              <>
                <StyledHint>{i18n.unverified}</StyledHint>
                <Link
                  variant={LINK_VARIANT_DEFAULT_NEGATIVE}
                  onClick={onVerifyClick}
                  data-mu-test='verify'
                >
                  {i18n.verifyLink}
                </Link>
              </>
            )}
            {variant === TRUSTED_SMS_INPUT_VARIANT_VERIFICATION && (
              <>
                <StyledHint>{i18n.verification}</StyledHint>
                <Link
                  variant={LINK_VARIANT_DEFAULT_NEGATIVE}
                  onClick={onVerifyClick}
                  data-mu-test='verify'
                >
                  {i18n.verifyLink}
                </Link>
              </>
            )}
            {variant === TRUSTED_SMS_INPUT_VARIANT_BLOCKED && (
              <>
                <StyledHint>{i18n.blocked}</StyledHint>
                <Link
                  variant={LINK_VARIANT_DEFAULT_NEGATIVE}
                  onClick={onVerifyClick}
                  data-mu-test='verify'
                >
                  {i18n.verifyLink}
                </Link>
              </>
            )}
          </StyledHintContainer>
        )}
      </StyledContainer>
    </>
  )
}

TrustedSmsInput.defaultProps = {
  i18n: {
    unverified: 'Not verified.',
    verification: 'Awaiting verification.',
    blocked: 'Blocked.',
    verifyLink: 'Request verification code',
    invalid: 'Wrong format'
  }
}

TrustedSmsInput.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  dataAttributeTest: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  aliasIcon: PropTypes.bool,
  onChange: PropTypes.func,
  onVerifyClick: PropTypes.func,
  onAliasClick: PropTypes.func,
  variant: PropTypes.oneOf([
    TRUSTED_SMS_INPUT_VARIANT_NEUTRAL,
    TRUSTED_SMS_INPUT_VARIANT_SUCCESS,
    TRUSTED_SMS_INPUT_VARIANT_UNVERIFIED,
    TRUSTED_SMS_INPUT_VARIANT_VERIFICATION,
    TRUSTED_SMS_INPUT_VARIANT_BLOCKED,
    TRUSTED_SMS_INPUT_VARIANT_INVALID
  ]),
  i18n: PropTypes.shape({
    unverified: PropTypes.string,
    verification: PropTypes.string,
    blocked: PropTypes.string,
    verifyLink: PropTypes.string,
    invalid: PropTypes.string
  })
}
